import { gql } from '@apollo/client';
import { Text } from '@mnd-frontend/ui';
import styled, { css } from 'styled-components';
import {
  useReportDynamicData,
  useReportEditable,
  useReportGraphHelpers,
  useReportStaticData,
  useReportTranslation,
} from '../../../hooks';
import { Section, TextInput } from '../../common';

export const REPORT2_SUMMARY_COVER_FRAGMENT = gql`
  fragment Report2SummaryCover on ReportSummaryCover {
    id
    title
    state
  }
`;

const Wrapper = styled.div<{ $hasImage?: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: ${props => (props.$hasImage ? '34rem' : '23rem')};
  color: white;
`;

const IconWrapper = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 7rem;
  width: 7rem;
  background: white;
  border-bottom-right-radius: 1rem;

  img {
    padding: 0.5rem;
    height: 100%;
    width: 100%;
    object-fit: contain;
    border-bottom-right-radius: 1.25rem;
    border-top-left-radius: 1.25rem;
  }
`;

const DescriptionWrapper = styled.div<{ $withTextShadow: boolean }>`
  align-self: flex-end;
  display: flex;
  flex-direction: column;
  padding: 0.5rem 0.5rem 0.5rem 4.5rem;
  text-align: right;
  text-transform: uppercase;
  ${props =>
    props.$withTextShadow &&
    css`
      text-shadow: 0 0.25rem 0.25rem rgba(0, 0, 0, 0.25);
    `}
`;

const SummaryCover = () => {
  const { id } = useReportStaticData();
  const { fromDate, toDate, brand } = useReportStaticData();
  const { updateReport, brandColor, coverImage, logo, title } = useReportDynamicData();

  const { t } = useReportTranslation();
  const { editable } = useReportEditable();

  const handleTitleChange = (title: string) => {
    updateReport({
      variables: {
        id,
        title,
      },
    });
  };

  const { getLegendCaption } = useReportGraphHelpers();

  return (
    <Section $bgColor={brandColor} $imgUrl={coverImage?.url ?? undefined}>
      <Wrapper $hasImage={!!coverImage}>
        {logo?.url && (
          <IconWrapper aria-hidden>
            <img src={logo.url} alt={brand} />
          </IconWrapper>
        )}
        <DescriptionWrapper $withTextShadow={!!coverImage}>
          <Text as={'h3'} variant={'reportHeadlineLarge'} mobileVariant="reportHeadlineMedium">
            {t('reports_brand_report')}
          </Text>
          <Text as={'h3'} variant={'reportHeadlineMedium'} mobileVariant="reportHeadlineSmall">
            {getLegendCaption({ fromDate, toDate })}
          </Text>
        </DescriptionWrapper>

        <Text as={'h1'} variant={'reportDisplayLarge'} mobileVariant={'reportHeadlineLarge'}>
          {editable ? (
            <TextInput
              initialValue={title}
              onChange={handleTitleChange}
              placeholder={t('common_title')}
            />
          ) : (
            title
          )}
        </Text>
      </Wrapper>
    </Section>
  );
};

export default SummaryCover;
